import { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, FileText, RefreshCcw, Send, ShieldCheck } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { API_BASE } from '@/lib/api-base';
import { useAuth } from '@/context/AuthContext';

type RuntimeSurface = 'self-service';

type RuntimeScreen = {
  context: {
    surface: RuntimeSurface;
    module: string;
    moduleSlot?: string;
    route: string;
    entityType?: string;
    profileTemplateCode?: string;
  };
  view: {
    mode: string;
    compiledLayout: Record<string, unknown>;
    interactionPatterns: Array<{ type: string; config: Record<string, unknown> }>;
  };
  capabilities: {
    allowedActions: Array<{
      id: string;
      label: string;
      actionType: string;
      endpointRef?: string;
      requiresConfirmation?: boolean;
    }>;
  };
};

type RuntimeDataResponse = {
  status: string;
  queryRef?: string;
  data: {
    rows?: Array<Record<string, unknown>>;
    pagination?: Record<string, unknown>;
  };
  metadata?: Record<string, unknown>;
};

type RuntimeTab = {
  id: string;
  label: string;
  queryRef: string;
};

type PortalModuleConfig = {
  module: string;
  slot: string;
  route: string;
  entityType: string;
  profileTemplateCode: string;
  title: string;
  subtitle: string;
};

const PORTAL_MODULES: Record<string, PortalModuleConfig> = {
  fm: {
    module: 'fm',
    slot: 'portal-view',
    route: '/portal/fm',
    entityType: 'freight_tariff',
    profileTemplateCode: 'fm-profile',
    title: 'Freight Customer Access',
    subtitle: 'Shipment updates, Penanshin tariff lists and shared documents for your company inside boox.app.',
  },
  tm: {
    module: 'tm',
    slot: 'portal-view',
    route: '/portal/tm',
    entityType: 'transport_job',
    profileTemplateCode: 'tm-profile',
    title: 'Transport Customer Access',
    subtitle: 'Jobs, documents and billing shared with your company inside boox.app.',
  },
};

function resolvePortalModuleConfig(moduleCode: string | undefined): PortalModuleConfig {
  const code = (moduleCode ?? 'tm').toLowerCase();
  return PORTAL_MODULES[code] ?? {
    module: code,
    slot: 'portal-view',
    route: `/portal/${code}`,
    entityType: `${code}_portal_record`,
    profileTemplateCode: `${code}-profile`,
    title: `${code.toUpperCase()} Customer Access`,
    subtitle: 'Shared operations, documents and billing for your company inside boox.app.',
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

function runtimeTabs(screen: RuntimeScreen | null): RuntimeTab[] {
  if (!screen) return [];
  for (const pattern of screen.view.interactionPatterns) {
    if (pattern.type !== 'tabbed') continue;
    const tabs = Array.isArray(pattern.config.tabs) ? pattern.config.tabs : [];
    return tabs.flatMap((tab) => {
      const item = asRecord(tab);
      const id = stringValue(item.id);
      const label = stringValue(item.label);
      const queryRef = stringValue(item.queryRef);
      return id && label && queryRef ? [{ id, label, queryRef }] : [];
    });
  }
  return [];
}

function formatValue(value: unknown): string {
  if (value == null || value === '') return '-';
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function columnLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function visibleColumns(rows: Array<Record<string, unknown>>): string[] {
  const blocked = new Set(['id', 'data', 'raw', 'metadata']);
  const preferred = [
    'tariff_family_label',
    'tariff_code',
    'tariff_name',
    'customer_name',
    'agent_name',
    'vendor_name',
    'trade_lane',
    'currency',
    'base_amount',
    'status',
    'valid_to',
    'job_number',
    'planned_date',
    'pickup',
    'delivery',
    'pod_status',
    'billing_status',
    'amount_sgd',
    'updated_at',
  ];
  const keys = new Set(rows.flatMap((row) => Object.keys(row)).filter((key) => !blocked.has(key)));
  const ordered = preferred.filter((key) => keys.has(key));
  const rest = [...keys].filter((key) => !ordered.includes(key)).slice(0, 8);
  return [...ordered, ...rest].slice(0, 10);
}

async function apiEnvelope<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });
  const body = await res.json() as { ok: boolean; data?: T; error?: { message?: string } };
  if (!res.ok || !body.ok || body.data == null) {
    throw new Error(body.error?.message ?? `Request failed (${res.status})`);
  }
  return body.data;
}

export function DynamicRuntimePortalPage() {
  const { moduleCode } = useParams();
  const { accessToken, user } = useAuth();
  const portalConfig = useMemo(() => resolvePortalModuleConfig(moduleCode), [moduleCode]);
  const [screen, setScreen] = useState<RuntimeScreen | null>(null);
  const [data, setData] = useState<RuntimeDataResponse | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const tabs = useMemo(() => runtimeTabs(screen), [screen]);
  const activeQueryRef = tabs.find((tab) => tab.id === activeTab)?.queryRef ?? tabs[0]?.queryRef ?? stringValue(screen?.view.compiledLayout.dataApiRef);
  const rows = data?.data.rows ?? [];
  const columns = visibleColumns(rows);
  const selectedRow = rows.find((row, index) => String(row.id ?? row.job_number ?? index) === selectedRowId) ?? rows[0] ?? null;
  const fmTariffExportReady = portalConfig.module === 'fm'
    && typeof activeQueryRef === 'string'
    && activeQueryRef.includes('tariffs');
  const exportTariffs = useCallback(async () => {
    if (!accessToken || !activeQueryRef) return;
    const params = new URLSearchParams({
      surface: 'self-service',
      queryRef: activeQueryRef,
    });
    if (activeTab) params.set('categoryId', activeTab);
    const res = await fetch(`${API_BASE}/api/v1/fm/tariffs/export.xlsx?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    if (!res.ok) {
      throw new Error(await res.text().catch(() => 'Tariff export failed'));
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `portal-${activeTab ?? 'tariffs'}.xlsx`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }, [accessToken, activeQueryRef, activeTab, portalConfig.module]);

  const loadRuntime = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams({
        surface: 'self-service',
        module: portalConfig.module,
        slot: portalConfig.slot,
        route: portalConfig.route,
        entityType: portalConfig.entityType,
        profileTemplateCode: portalConfig.profileTemplateCode,
      });
      const nextScreen = await apiEnvelope<RuntimeScreen>(`/api/v1/dynamic-runtime/screen?${query.toString()}`, accessToken);
      const nextTabs = runtimeTabs(nextScreen);
      const nextActiveTab = activeTab && nextTabs.some((tab) => tab.id === activeTab) ? activeTab : nextTabs[0]?.id ?? null;
      const nextQueryRef = nextTabs.find((tab) => tab.id === nextActiveTab)?.queryRef ?? stringValue(nextScreen.view.compiledLayout.dataApiRef);
      let nextData: RuntimeDataResponse | null = null;
      if (nextQueryRef) {
        nextData = await apiEnvelope<RuntimeDataResponse>('/api/v1/dynamic-runtime/data/query', accessToken, {
          method: 'POST',
          body: JSON.stringify({
            surface: 'self-service',
            module: portalConfig.module,
            slot: portalConfig.slot,
            route: portalConfig.route,
            entityType: portalConfig.entityType,
            profileTemplateCode: portalConfig.profileTemplateCode,
            queryRef: nextQueryRef,
            categoryId: nextActiveTab,
            pagination: { page: 1, pageSize: 50 },
            filters: {},
            sort: {},
          }),
        });
      }
      setScreen(nextScreen);
      setActiveTab(nextActiveTab);
      setData(nextData);
      setSelectedRowId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load runtime portal');
    } finally {
      setLoading(false);
    }
  }, [accessToken, activeTab, portalConfig]);

  useEffect(() => {
    void loadRuntime();
  }, [loadRuntime]);

  const changeTab = async (tab: RuntimeTab) => {
    if (!accessToken) return;
    setActiveTab(tab.id);
    setActionMessage(null);
    setLoading(true);
    try {
      const nextData = await apiEnvelope<RuntimeDataResponse>('/api/v1/dynamic-runtime/data/query', accessToken, {
        method: 'POST',
        body: JSON.stringify({
          surface: 'self-service',
          module: portalConfig.module,
          slot: portalConfig.slot,
          route: portalConfig.route,
          entityType: portalConfig.entityType,
          profileTemplateCode: portalConfig.profileTemplateCode,
          queryRef: tab.queryRef,
          categoryId: tab.id,
          pagination: { page: 1, pageSize: 50 },
          filters: {},
          sort: {},
        }),
      });
      setData(nextData);
      setSelectedRowId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load tab');
    } finally {
      setLoading(false);
    }
  };

  const runAction = async (actionRef: string) => {
    if (!accessToken) return;
    setActionMessage(null);
    try {
      if (actionRef === 'fm.export.excel') {
        await exportTariffs();
        setActionMessage('Tariff workbook downloaded.');
        return;
      }
      await apiEnvelope('/api/v1/dynamic-runtime/action/execute', accessToken, {
        method: 'POST',
        body: JSON.stringify({
          surface: 'self-service',
          module: portalConfig.module,
          slot: portalConfig.slot,
          route: portalConfig.route,
          entityType: portalConfig.entityType,
          entityId: stringValue(selectedRow?.id) ?? stringValue(selectedRow?.job_number),
          profileTemplateCode: portalConfig.profileTemplateCode,
          actionRef,
          payload: {
            selected: selectedRow,
            requestedBy: user?.email,
            channel: 'self-service',
          },
          mode: 'execute',
        }),
      });
      setActionMessage(actionRef === 'tm.portal.download-document' ? 'Document prepared for download.' : 'Request sent to operations.');
    } catch (err) {
      setActionMessage(err instanceof Error ? err.message : 'Action failed');
    }
  };

  if (loading && !screen) {
    return <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">Loading portal workspace...</div>;
  }

  if (error && !screen) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6">
        <div className="text-sm font-semibold text-red-800">Dynamic portal unavailable</div>
        <div className="mt-1 text-sm text-red-700">{error}</div>
        <button onClick={() => void loadRuntime()} className="mt-4 inline-flex items-center gap-2 rounded-md border border-red-200 bg-white px-3 py-2 text-sm text-red-700">
          <RefreshCcw className="h-4 w-4" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-950">{portalConfig.title}</h1>
          <p className="mt-1 text-sm text-slate-500">{portalConfig.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {fmTariffExportReady ? (
            <button
              onClick={() => void exportTariffs().catch((err) => setActionMessage(err instanceof Error ? err.message : 'Tariff export failed'))}
              className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              <Download className="h-4 w-4" />
              Download Excel
            </button>
          ) : null}
          <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-800">
            <ShieldCheck className="h-4 w-4" />
            Secure customer access
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
          <div className="flex flex-wrap gap-1">
            {tabs.map((tab) => {
              const active = (activeTab ?? tabs[0]?.id) === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => void changeTab(tab)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium ${active ? 'bg-slate-950 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          <button onClick={() => void loadRuntime()} className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50">
            <RefreshCcw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px] text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                {columns.map((key) => <th key={key} className="px-3 py-2 font-semibold">{columnLabel(key)}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? rows.map((row, index) => {
                const rowId = String(row.id ?? row.job_number ?? index);
                const selected = selectedRowId ? selectedRowId === rowId : index === 0;
                return (
                  <tr
                    key={rowId}
                    onClick={() => setSelectedRowId(rowId)}
                    className={`cursor-pointer border-b border-slate-100 hover:bg-slate-50 ${selected ? 'bg-sky-50' : ''}`}
                  >
                    {columns.map((key) => (
                      <td key={key} className="max-w-[180px] truncate px-3 py-2 text-slate-700">
                        {formatValue(row[key])}
                      </td>
                    ))}
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={Math.max(columns.length, 1)} className="px-3 py-8 text-center text-sm text-slate-400">
                    {loading ? 'Loading rows...' : 'No portal rows returned.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900">
            <FileText className="h-4 w-4" />
            Selected Record
          </div>
          {selectedRow ? (
            <dl className="grid gap-2 text-sm md:grid-cols-2">
              {Object.entries(selectedRow).slice(0, 12).map(([key, value]) => (
                <div key={key} className="rounded-md bg-slate-50 px-3 py-2">
                  <dt className="text-xs font-medium uppercase text-slate-400">{columnLabel(key)}</dt>
                  <dd className="mt-1 truncate text-slate-800">{formatValue(value)}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="text-sm text-slate-500">Select a row to view details.</p>
          )}
        </div>

        <aside className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-sm font-semibold text-slate-900">Actions</div>
          <div className="mt-3 space-y-2">
            {(screen?.capabilities.allowedActions ?? []).map((action) => (
              <button
                key={action.id}
                onClick={() => action.endpointRef ? void runAction(action.endpointRef) : undefined}
                className="flex w-full items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                <span>{action.label}</span>
                {action.actionType === 'download' ? <Download className="h-4 w-4" /> : <Send className="h-4 w-4" />}
              </button>
            ))}
          </div>
          {actionMessage ? <div className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">{actionMessage}</div> : null}
        </aside>
      </div>
    </section>
  );
}
